<template>
<div>
  <cytomine-modal :active="active" :title="$t('edit-case')" @close="$emit('update:active', false)">
  <div>{{ $t('name') }}</div>
  <b-input v-model="caseInfo.name" name="name" />
  <div>{{ $t('status') }}</div>
  <div>
    <b-dropdown position="is-bottom-right" scrollable :max-height="350">
      <template #trigger>
        <button class="button is-small is-link">
          <i class="fas fa-caret-down"></i>
          <span style="margin-left: 5px;">{{ DefaultWorklist[caseInfo.status] }}</span>
        </button>
      </template>
      <b-dropdown-item v-for="item in statusList" :key="item.id" @click="selectStatus(item)" >
        {{ item.label }}
      </b-dropdown-item>
    </b-dropdown>
  </div>
  <div>{{ $t('assignee') }}</div>
  <div>
    <b-dropdown position="is-bottom-right" scrollable :max-height="350">
      <template #trigger>
        <button class="button is-small is-link">
          <i class="fas fa-caret-down"></i>
          <span style="margin-left: 5px;">{{ caseInfo.assigneeFullName }}</span>
        </button>
      </template>
      <b-dropdown-item v-for="item in userList" :key="item.id" @click="selectAssignee(item)" >
        {{ item.fullName }}
      </b-dropdown-item>
    </b-dropdown>
  </div>
  <div>{{ $t('anatomic-site') }}</div>
  <b-input v-model="caseInfo.part" name="part" />
  <div>{{ $t('bench') }}</div>
  <b-input v-model="caseInfo.bench" name="bench" />
  <div>{{ $t('patient-his-id') }}</div>
  <div class="display-flex-row">
    <div style="width: 48%;">
      <b-input v-model="caseInfo.patientHisId" name="patientHisId" />
      <div style="display: flex; justify-content: flex-end; margin-top: 5px;">
        <b-button @click="searchPatient">{{ $t('search') }}</b-button>
      </div>
    </div>
    <div style="width: 48%;">
      <label> <input type="checkbox" v-model="isCreatePatient" @click="toggleCreatePatient" /> {{ $t('create-patient') }} </label>
      <div>
        <div style="color:gray">{{ $t('patient-his-id') }}</div>
        <b-input type="text" v-model="patientInfo.patientHisId" :disabled="!isCreatePatient" />
        <div style="color:gray">{{ $t('patient') }} {{ $t('name') }}</div>
        <b-input type="text" v-model="patientInfo.name" />
        <div style="color:gray">{{ $t('patient') }} {{ $t('gender') }}</div>
        <b-field>
          <b-radio-button
            v-for="option in GenderOptions"
            :key="option.value"
            v-model="patientInfo.gender"
            :native-value="option.value"
          >
            {{ option.label }}
          </b-radio-button>
        </b-field>
        <div style="color:gray">{{ $t('patient') }} {{ $t('birth') }}</div>
        <b-datepicker
          v-model="patientInfo.birth"
          icon="calendar"
          append-to-body
          class="date-picker"
        />
        <b-button @click="createPatient" :disabled="!isPatientFormValid || !isCreatePatient">{{ $t('button-add') }}</b-button>
        <b-button @click="updatePatient" :disabled="!isPatientFormValid || !isSearchedResultPatientInfo">{{ $t('button-update') }}</b-button>
        <b-button @click="deletePatient" :disabled="!isSearchedResultPatientInfo">{{ $t('button-delete') }}</b-button>
      </div>
    </div>
  </div>

  <template #footer>
    <button class="button" type="button" @click="$emit('update:active', false)">{{ $t('button-cancel') }}</button>
    <button class="button is-link" type="button"  @click="saveUpdatedCaseInfo">Save</button>
  </template>
  </cytomine-modal>
</div>
</template>

<script>
import CytomineModal from '@/components/utils/CytomineModal';
import { PatientCollection, Patient, UserCollection, Project } from 'cytomine-client';
import DefaultWorklist from '../../enums/DefaultWorklist';
import {fullName} from '@/utils/user-utils.js';
import { GenderOptions } from '../../enums/DefaultEnums';

export default {
  name: 'edit-project-modal',
  props: {
    revision : {type: Number, default: 0},
    active: Boolean,
    case: Object,
  },
  components: {CytomineModal},
  data() {
    return {
      DefaultWorklist,
      GenderOptions,
      isCreatePatient: false,
      isSearchedResultPatientInfo: false,
      caseInfo: {
        id: null,
        name: '',
        patientId: null, 
        patientHisId: '', // HIS ID
        part: '',
        bench: '',
        assigneeFullName: '',
        assigneeid: null,
        status: null, // status enum
      },
      patientInfo: {
        id: null,
        patientHisId: '', // HIS ID
        name: '',
        gender: '',
        birth: null,
      },
      userList: [],
    };
  },
  computed: {
    statusList() {
      // 숫자 키만 추출해서 id, label 배열 만들기
      return Object.keys(DefaultWorklist)
        .filter((key) => !isNaN(key) && Number(key) !== 0)
        .map((key) => ({
          id: Number(key),
          label: DefaultWorklist[key],
        }));
    },
    isPatientFormValid() {
      const p = this.patientInfo;
      return ( p.patientHisId && p.name && p.gender && p.birth );
    }
  },
  watch: {
    async active(newVal) {
      if (newVal) {
        // 사용자 목록 불러오기
        let users = (await UserCollection.fetchAll()).array;
        users.forEach(u => u.fullName = fullName(u));
        console.log('user collection', users);
        this.userList = users;

        // 환자 정보 초기화
        this.patientInfo = {
          id: null,
          patientHisId: '', // HIS ID
          name: '',
          gender: '',
          birth: null,
        };

        // 모달이 열릴 때마다 case 복사
        this.caseInfo = { ...this.case };
        console.log('init case', this.case);
        const assigneeObj = this.userList.find(user => user.id === this.caseInfo.assigneeid);
        if (assigneeObj) {
          this.caseInfo.assigneeFullName = assigneeObj.fullName;
        }

        const testResult = await new Project({id: this.case.id}).fetch();
        console.log('fetch project result', testResult);
      }
    }
  },
  methods: {
    async saveUpdatedCaseInfo() {
      const patientInfo = {
        id: this.caseInfo.id,
        name: this.caseInfo.name,
        patientId: this.caseInfo.patientId,
        part: this.caseInfo.part,
        bench: this.caseInfo.bench,
        assigneeid: this.caseInfo.assigneeid,
        status: this.caseInfo.status,
        oldAssigneeId: this.case.assigneeid,
      };

      try {
        let result = await new Project().updateCase(patientInfo);
        console.log('result', result);

        // 성공 처리 (200)
        this.$notify({
          type: 'success',
          text: this.$t('notif-case-update-success')
        });

        this.$emit('update:revision', this.revision + 1);
        this.$emit('update:active', false);
        this.$root.$eventBus.$emit('refreshWorklist');
      }
      catch (err) {
        // status code 기반 처리
        const status = err?.response?.status;

        if (status === 409) {
          this.$notify({
            type: 'error',
            text: this.$t('notif-case-name-duplicate')
          });
        }
        else {
          this.$notify({
            type: 'error',
            text: this.$t('notif-case-update-fail')
          });
        }
      }
    },
    async searchPatient() {
      try {
        this.isCreatePatient = false;
        let result = await PatientCollection.searchByHisId(this.caseInfo.patientHisId);
        this.isSearchedResultPatientInfo = true;

        const p = result.collection[0];
        this.patientInfo = {
          ...p,
          birth: p.birth ? new Date(p.birth) : null
        };
        
        this.caseInfo.patientId = this.patientInfo.id; // case의 patientHisId도 HIS ID로 설정
        this.caseInfo.patientHisId = this.patientInfo.patientHisId; // case의 patientHisId도 HIS ID로 설정
      }
      catch (err) {
        if (err.response && err.response.status === 404) {
          this.$notify({type: 'error', text: this.$t('no-patient-found-by-his-id')});
        }
        else {
          console.error('검색 중 오류 발생:', err);
        }
      }
    },
    async createPatient() {
      const payload = {
        ...this.patientInfo,
        birth: this.formatDate(this.patientInfo.birth)
      };
      try {
        const saved = await new Patient(payload).save();
        this.caseInfo.patientId = saved.id;
        this.caseInfo.patientHisId = saved.patientHisId;
        this.$notify({type: 'success', text: this.$t('notif-patient-create-success')});
      }
      catch (err) {
        if (err.response && err.response.status === 409) {
          this.$notify({type: 'error', text: this.$t('notif-patient-his-id-duplicate')});
        } 
        else {
          this.$notify({type: 'error', text: this.$t('notif-patient-create-fail')});
        }
      }
    },
    async updatePatient() {
      const payload = {
        ...this.patientInfo,
        birth: this.formatDate(this.patientInfo.birth)
      };
      try {
        await new Patient(payload).update();
        this.$notify({ type: 'success', text: this.$t('notif-patient-update-success') });
      }
      catch (err) {
        this.$notify({ type: 'error', text: this.$t('notif-patient-update-fail') });
      }
    },
    async deletePatient() {
      try {
        await new Patient(this.patientInfo).delete();
        this.initializePatientInfo();
        this.$notify({ type: 'success', text: this.$t('notif-patient-delete-success') });
      }
      catch (err) {
        this.$notify({ type: 'error', text: this.$t('notif-patient-delete-fail') });
      }
    },
    selectStatus(item) {
      this.caseInfo = { ...this.caseInfo, status: item.id };
    },
    selectAssignee(item) {
      this.caseInfo = { ...this.caseInfo, assigneeFullName: item.fullName, assigneeid: item.id };
    },
    toggleCreatePatient() {
      this.isCreatePatient = !this.isCreatePatient;
      if (this.isCreatePatient) {
        this.isSearchedResultPatientInfo = false;
      }
      this.initializePatientInfo();
    },
    initializePatientInfo() {
      this.patientInfo = {
        id: null,
        patientHisId: null,
        name: '',
        gender: null,
        birth: null,
      };
    },
    formatDate(date) {
      if (!date) return null;

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd}`;
    }
  },
  async created() {
  }
};

</script>

<style scoped>

.display-flex-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>